import { EntityDashboardHeader } from "./components/EntityDashboardHeader";
import { useEntityManagerEnhancedBorders } from "./hooks/useEntityManagerEnhancedBorders";
import { EntityManagerProvider, useEntityManagerContext } from "./context/EntityManagerContext";
import { DashboardConfiguratorProvider, useConfiguratorContext } from "../entityViews/dashboardConfigurator/context/DashboardConfiguratorContext";
import { DashboardConfiguratorModal } from "../entityViews/dashboardConfigurator";
import { EntityFileProvider } from "../entityViews/files";
import { Drawer, DrawerContent, DrawerTitle } from "@/modern/components/ui/drawer";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { LayoutGrid } from "lucide-react";

const EntityManager = () => {
  const { record, entityConfig, entityType } = useEntityManagerContext();
  const { savedVisibleTiles, savedColumns, savedFontPreset, isSaving } = useConfiguratorContext();
  const [configOpen, setConfigOpen] = useState(false);

  return (
    <div className="entity-page flex bg-background h-full flex-1 w-full min-w-0 [&_.swivu-modern-grid-tbar]:bg-transparent absolute inset-0" style={{ zoom: savedFontPreset === "war-room" ? 1.3 : 1 }}>
      <div className="flex-1 min-w-0 flex flex-col overflow-y-auto">
        <EntityDashboardHeader setConfigOpen={setConfigOpen} />
        <main className="flex-1 space-y-4">
          {savedVisibleTiles.length > 0 ? (
            <div
              key={`${entityType}-${record.id}`}
              className="grid gap-9 px-4 py-4"
              style={{
                gridTemplateColumns: `repeat(${savedColumns}, 1fr)`,
                gridAutoRows: "13.33rem",
                // gridAutoFlow dense handles any tiles that still lack
                // explicit positions (legacy configs not yet opened in the
                // configurator). Once opened, backfillPositions assigns x/y
                // and explicit placement takes over.
                gridAutoFlow: "dense",
              }}
            >
              <EntityFileProvider record={record} entityType={entityType}>
                {savedVisibleTiles.map((tile) => {
                  const widget = entityConfig.widgets.find((w) => w.id === tile.id);
                  if (!widget) return null;
                  const w = Math.min(tile.colSpan, savedColumns);
                  return (
                    <div
                      key={tile.id}
                      style={
                        tile.x !== undefined && tile.y !== undefined
                          ? {
                              gridColumn: `${tile.x + 1} / span ${w}`,
                              gridRow: `${tile.y + 1} / span ${tile.rowSpan}`,
                            }
                          : {
                              gridColumn: `span ${w}`,
                              gridRow: `span ${tile.rowSpan}`,
                            }
                      }
                      className="min-h-0 overflow-auto"
                    >
                      <widget.component record={record} entityType={entityType} />
                    </div>
                  );
                })}
              </EntityFileProvider>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center px-4 pb-4 min-h-[20rem]">
              <div className="flex flex-col items-center gap-3 text-center">
                <LayoutGrid className="size-8 text-muted-foreground/40" />
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-muted-foreground">No widgets visible</h3>
                  <p className="text-xs text-muted-foreground/70 max-w-[18rem]">
                    Choose a layout preset from the grid icon above, or configure your dashboard.
                  </p>
                </div>
                <Button size="sm" variant="outline" className="mt-1" onClick={() => setConfigOpen(true)}>
                  Configure Dashboard
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      <DashboardConfiguratorModal open={configOpen} onOpenChange={setConfigOpen} />

      <AnimatePresence>
        {isSaving && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex flex-col items-center gap-5"
            >
              <div className="grid grid-cols-3 gap-1.5 w-16 h-16">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <motion.div
                    key={i}
                    className="rounded-sm bg-primary/60"
                    initial={{ scale: 0.8, opacity: 0.3 }}
                    animate={{
                      scale: [0.8, 1.2, 0.8],
                      opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.08,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>
              <p className="text-sm font-medium text-muted-foreground">Applying layout...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const snapPoints = [0.23, 1];
const defaultSnapPoint = snapPoints[1];

export const EntityManagerView = () => {
  const { entityManager, editorHandler } = useSwivuBryntumContext();
  const { isOpen: isEntityManagerOpen, entityId, entityType } = entityManager;
  const [enhancedBordersEnabled] = useEntityManagerEnhancedBorders();
  const [snap, setSnap] = useState<number | string | null>(defaultSnapPoint);
  const hasOpenedEditors = editorHandler.editors.length > 0;
  const isDragging = useRef(false);

  const setSnapPointToDefault = () => {
    if (snap !== defaultSnapPoint) {
      setSnap(defaultSnapPoint);
    }
  }

  /* sets pointer events properties: pointer-events: auto; on the body to allow interactions with modal dialogs
  Found resolution here: https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724*/
  useEffect(() => {
    if (isEntityManagerOpen) {
      setTimeout(() => {
        document.body.style.pointerEvents = "auto";
      });
    }
  }, [isEntityManagerOpen]);

  useEffect(() => {
    if (isEntityManagerOpen) {
      setSnapPointToDefault(); // Reset to default snap point when drawer opens or the entity id or type changes
    }
  }, [entityId, entityType, isEntityManagerOpen])

  /* We need to use this hack to prevent the drawer from blocking interactions with modal dialogs, if not applied the input fields in the @TaskGeneralEditor.tsx are not focusable
  found resolution here: https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724
  */
  useLayoutEffect(() => {
    document.addEventListener("focusin", (e) => e.stopImmediatePropagation());
    document.addEventListener("focusout", (e) => e.stopImmediatePropagation());
  }, []);

  return (
    <Drawer
      modal={false}
      open={isEntityManagerOpen}
      onOpenChange={(open) => {
        if (!open) {
          entityManager.closeEntityManager();
        }
      }}
      snapPoints={snapPoints}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      snapToSequentialPoint={true}
      closeThreshold={0.5} /* Close if user drags down 50% of the height of the drawer */
      dismissible={!hasOpenedEditors} /* Prevent the drawer from closing when any editor is opened */
    >
      {/* The !select-text class needs to be applied to enable user selection in the content inside of the drawer component, the vaul library disables it by default.
      Found resolution here: https://github.com/emilkowalski/vaul/issues/346#issuecomment-2132384928 */}
      <DrawerContent
        className="h-[calc(100vh-3.5rem)] !select-text outline-none"
        data-enhanced-borders={enhancedBordersEnabled || undefined}
      >
        <DrawerTitle className="sr-only">Entity Manager</DrawerTitle>
        <div
          onMouseDown={() => isDragging.current = false}
          onMouseMove={() => isDragging.current = true}
          onClick={() => !isDragging.current && entityManager.closeEntityManager()}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-50 cursor-pointer p-4 -m-4"
          aria-label="Close drawer"
        >
          <div className="h-[0.325rem] w-[6.6rem] bg-accent-contrast hover:bg-accent-contrast/90 transition-colors rounded-full" />
        </div>
        <EntityManagerProvider>
          <DashboardConfiguratorProvider>
            <EntityManager />
          </DashboardConfiguratorProvider>
        </EntityManagerProvider>
      </DrawerContent>
    </Drawer>
  );
};
